import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip, Divider, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { AutoStoriesOutlined, ExpandMore } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import { PageTitleWithButtons } from '../../components/shared/layout/PageTitleWithButtons';
import { EmptyTable } from '../../components/shared/EmptyTable';
import CustomPagination from '../../components/forms/Pagination';
import { UserContext } from '../../components/shared/useUser';
import useNswagClient from '../../hooks/api/useNswagClient';
import { recipeV2Client, RecipeV2ClientMocked, RecipeStatus, AllergenStatus } from '../../app/services/api/recipeV2';
import { RecipeBankAllergenFilter } from '../../app/services/api/recipeV2/recipeBankAllergenFilter';
import { mockAllergens, mockRecipesV2 } from '../../mocks/data/recipeV2Data';
import { isDemoModeEnabled } from '../../utils/featureAccess';
import RecipeBankCard from './components/RecipeBankCard';
import GenerateRecipeBookDialog from './components/GenerateRecipeBookDialog';
import useRecipeBankList from './hooks/useRecipeBankList';
import { DietaryAttributeReference, RecipeV2 } from './types';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;
const PAGE_SIZE = 12;

/**
 * Recipe Bank: a read-only catalogue of Active Recipe V2 records. Demo Mode uses Thomas Franks
 * data for offline browsing; recipe authoring stays on the existing /range/recipes pages.
 */
const RecipeBankPage = () => {
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const nswagApi = useNswagClient();
  const demoMode = isDemoModeEnabled();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const canPublish = hasPermissionTo(['RecipeV2Read']) && hasPermissionTo(['RecipeV2Export']);

  const [dietaryOptions, setDietaryOptions] = useState<DietaryAttributeReference[]>([]);
  const [dietaryFilter, setDietaryFilter] = useState<DietaryAttributeReference[]>([]);
  const [allergenCode, setAllergenCode] = useState('');
  const [allergenStatus, setAllergenStatus] = useState<AllergenStatus>(AllergenStatus.FreeFrom);

  const { recipes, totalCount, isLoading, error, loadRecipes } = useRecipeBankList();

  useEffect(() => {
    const franchiseId = selectedStore?.franchiseId;

    // Dietary attribute IDs are franchise-scoped. Loading every visible franchise here can
    // produce duplicate labels with different IDs and send the wrong ID to the bank filter.
    setDietaryFilter([]);
    setPage(0);

    if (!franchiseId && !demoMode) {
      setDietaryOptions([]);
      return;
    }

    let active = true;
    (demoMode
      ? new RecipeV2ClientMocked().getDietaryAttributeReference(franchiseId)
      : recipeV2Client.getDietaryAttributeReference(franchiseId))
      .then((options) => {
        if (active) setDietaryOptions(options);
      })
      .catch(() => {
        if (active) setDietaryOptions([]);
      });

    return () => {
      active = false;
    };
  }, [selectedStore?.franchiseId, demoMode]);

  useEffect(() => {
    if (demoMode) {
      setCategoryOptions(Array.from(new Set(mockRecipesV2
        .filter((recipe) => recipe.status === RecipeStatus.Active)
        .map((recipe) => recipe.primaryCategory))).sort((left, right) => left.localeCompare(right)));
      return;
    }
    if (selectedStore?.storeNumber) {
      nswagApi.getPrimaryCategoriesForStore(selectedStore.storeNumber)
        .then((res) => {
          const names = (res?.data ?? [])
            .map((c: { primaryCategoryName?: string }) => c.primaryCategoryName ?? '')
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCategoryOptions(names);
        })
        .catch(() => setCategoryOptions([]));
    }
  }, [selectedStore?.storeNumber, demoMode]);

  useEffect(() => {
    loadRecipes(
      debouncedSearch,
      category,
      dietaryFilter.map((attribute) => attribute.id),
      page,
      PAGE_SIZE,
      demoMode && allergenCode ? { code: allergenCode, status: allergenStatus } as RecipeBankAllergenFilter : undefined,
    );
  }, [debouncedSearch, category, dietaryFilter, allergenCode, allergenStatus, demoMode, page, loadRecipes]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
        setDebouncedSearch(value);
        setPage(0);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setCategory('');
    setDietaryFilter([]);
    setAllergenCode('');
    setAllergenStatus(AllergenStatus.FreeFrom);
    setPage(0);
  };

  const handleOpenRecipe = (recipe: RecipeV2) => {
    navigate(`/range/recipe-bank/${recipe.id}`);
  };

  const maxPages = Math.ceil(totalCount / PAGE_SIZE);
  const hasFilters = Boolean(search || category || dietaryFilter.length || allergenCode);
  // Top-of-page quick filter - a handful of the most common categories as pill chips.
  // The full category list (plus dietary/allergens) stays in Advanced Filters below.
  const quickCategoryOptions = categoryOptions.slice(0, 4);
  const handleQuickCategoryClick = (option: string) => {
    setCategory((current) => (current === option ? '' : option));
    setPage(0);
  };

  return (
    <PageArea>
      <PageTitleWithButtons
        title={t('recipeBank.title', 'Recipe Bank')}
        subTitle={t('recipeBank.subtitle', 'Browse the full recipe catalogue')}
      >
        {canPublish && (
          <Button
            variant="primary"
            startIcon={<AutoStoriesOutlined />}
            onClick={() => setBookDialogOpen(true)}
          >
            {t('recipeBank.book.generate', 'Publish recipes')}
          </Button>
        )}
      </PageTitleWithButtons>

      <Box sx={{ mt: 6 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }}
          spacing={4}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          divider={quickCategoryOptions.length > 0
            ? <Divider orientation="vertical"
              flexItem />
            : undefined}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder={t('searchPlaceholder')}
            inputProps={{ 'aria-label': t('searchPlaceholder') }}
            sx={{ width: { xs: '100%', sm: 340 }, flexShrink: 0 }}
          />

          {quickCategoryOptions.length > 0 && (
            <Stack direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap>
              {quickCategoryOptions.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  clickable
                  color={category === option ? 'primary' : 'default'}
                  variant={category === option ? 'filled' : 'outlined'}
                  onClick={() => handleQuickCategoryClick(option)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <Accordion variant="borderless"
          disableGutters
          sx={{ mt: 3 }}>
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="textSM"
                  fontWeight={600}>{t('recipeBank.advancedFilters', 'Advanced Filters')}</Typography>
                <ExpandMore />
              </Box>
              <Box onClick={(event) => event.stopPropagation()}>
                <Button
                  size="md"
                  variant="secondary"
                  disabled={!hasFilters}
                  onClick={handleClearFilters}
                >
                  {t('recipeBank.clearFilters', 'Clear Filters')}
                </Button>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container
              spacing={3}>
              <Grid item
                xs={12}
                sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('category')}
                  value={category}
                  onChange={(event) => { setCategory(event.target.value); setPage(0); }}
                >
                  <MenuItem value="">{t('recipeBank.allCategories', 'All categories')}</MenuItem>
                  {categoryOptions.map((option) => (
                    <MenuItem key={option}
                      value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                sm={6}>
                <Autocomplete
                  multiple
                  size="small"
                  options={dietaryOptions}
                  value={dietaryFilter}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_event, value) => {
                    setDietaryFilter(value);
                    setPage(0);
                  }}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('recipeBank.dietaryFilter', 'Dietary')} />
                  )}
                />
              </Grid>
              {demoMode && (
                <>
                  <Grid item
                    xs={12}
                    sm={6}>
                    <TextField select
                      fullWidth
                      size="small"
                      label={t('recipeBank.allergenFilter')}
                      value={allergenCode}
                      onChange={(event) => { setAllergenCode(event.target.value); setPage(0); }}>
                      <MenuItem value="">{t('recipeBank.allAllergens')}</MenuItem>
                      {mockAllergens.map((allergen) => (
                        <MenuItem key={allergen.code}
                          value={allergen.code}>{allergen.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item
                    xs={12}
                    sm={6}>
                    <TextField select
                      fullWidth
                      size="small"
                      label={t('recipeBank.allergenState')}
                      value={allergenStatus}
                      disabled={!allergenCode}
                      onChange={(event) => { setAllergenStatus(event.target.value as AllergenStatus); setPage(0); }}>
                      {[AllergenStatus.FreeFrom, AllergenStatus.Contains, AllergenStatus.MayContain, AllergenStatus.Unknown].map((status) => (
                        <MenuItem key={status}
                          value={status}>{t(`recipeBank.allergenStates.${status}`)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      {error && (
        <Alert severity="error"
          sx={{ mt: 4 }}>{t('recipeBank.loadError', 'Unable to load the recipe bank. Please try again.')}</Alert>
      )}

      <Box sx={{ mt: 6 }}>
        {isLoading && recipes.length === 0 ? (
          <Typography color="text.secondary">{t('loading')}</Typography>
        ) : recipes.length === 0 ? (
          <EmptyTable
            text={t('recipeBank.title', 'Recipe Bank')}
            onClear={handleClearFilters}
            onAdd={() => undefined}
            addItemText=""
            onAddIsCreating={false}
            showOnAdd={false}
            showClear={hasFilters}
            withPaginationShown={false}
          />
        ) : (
          <Grid container
            spacing={4}>
            {recipes.map((recipe) => (
              <Grid item
                key={recipe.id}
                xs={12}
                sm={6}
                md={4}
                lg={3}>
                <RecipeBankCard
                  recipe={recipe}
                  onOpen={handleOpenRecipe}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {maxPages > 1 && (
        <Box sx={{ mt: 4 }}>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          />
        </Box>
      )}

      <GenerateRecipeBookDialog
        open={bookDialogOpen}
        onClose={() => setBookDialogOpen(false)}
      />
    </PageArea>
  );
};

export default RecipeBankPage;
